import React, { useState, useCallback } from 'react';

import { getMediaObjectFromUploads } from 'actions/addPost';
import { openGifModalAction } from 'actions/gifModal';
import EditCommentComponent from 'components/Comment/EditCommentComponent';
import useCommentReducer from 'components/Comment/useCommentReducer';
import EnterComment from 'components/feed/EnterComment';
import { useTagRecommendations, useVisibilityManuallyUpdated } from 'components/hooks/post';
import { useMediaUploadStateComment } from 'components/hooks/useMediaUploadState';
import { useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import MediaPreview from 'screens/home/addPost/MediaPreview';
import UploadTypesView from 'screens/home/addPost/UploadTypesView';
import styled from 'styled-components';

import { ERR_MESS_CREATE_COMMENT } from 'constants/ProjectConstants';

import { createLevelUpCompetencyComment } from 'services/growth/oneOnOne';
import { updateEditorStatePredictTagsAndVisbility } from 'services/helpers/post';
import { showErrorSnackbar } from 'services/snackbar';

const CreateCompetencyDiscussion = ({
  loading,
  addComment: addNewComment,
  onAddComment = null,
  className,
  isDisabled = false,
  reporteeId: employeeId,
  competencyId,
  levelUpId,
  subCompetencyId,
}) => {
  const dispatch = useDispatch();
  // const [busy, setBusy] = useState(false);
  const {
    commentState,
    setEditorState,
    toggleIsUpdating: toggleIsCommentUpdating,
    setUploadedFile,
    clearComment,
    setGif,
    updateVisibility,
    updateSentiment,
    addTag,
  } = useCommentReducer(null, []);

  const { recommendations, updateTags } = useTagRecommendations();
  const { editorState, images = [], gifs = [], isUpdating: isCommentUpdating, visibility, sentiment } = commentState;

  const [isEnterCommentActive, setEnterCommentActive] = useState(false);

  const uploadedFile = images[0];
  const uploadedGif = gifs[0];
  const { fileUrl, deleteFile, uploadFile, uploading, setFileObject } = useMediaUploadStateComment({
    setImage: setUploadedFile,
    uploadedImage: uploadedFile,
    setGif,
  });

  const { visibilityManuallyUpdated, updateVisibilityManuallyStatus } = useVisibilityManuallyUpdated();

  const [commentAsKudos, setCommentAsKudos] = useState(false);

  const updateComments = (comment) => {
    // scrollToComments();
    addNewComment(comment);
  };

  const loadComments = useCallback(
    (comment) => {
      updateComments(comment);
    },
    [updateComments]
  );

  const addComment = useCallback(() => {
    if (!editorState.getCurrentContent().hasText()) {
      setEnterCommentActive(false);
      return;
    }
    toggleIsCommentUpdating();
    const media = getMediaObjectFromUploads(images, gifs);
    createLevelUpCompetencyComment({
      commentEditorState: editorState,
      media,
      sentiment,
      isKudos: commentAsKudos,
      employeeId,
      competencyId,
      levelUpId,
      subCompetencyId,
    })
      .then(({ comment }) => {
        if (typeof loadComments === 'function') loadComments(comment);
        setEnterCommentActive(false);
        setFileObject(null); // setting uploaded file object that is just in the local state to null
        if (typeof clearComment === 'function') clearComment();
        onAddComment && onAddComment();
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message || ERR_MESS_CREATE_COMMENT));
        toggleIsCommentUpdating();
      });
  }, [
    clearComment,
    editorState,
    dispatch,
    loadComments,
    setFileObject,
    // pageType,
    setEnterCommentActive,
    gifs,
    images,
    toggleIsCommentUpdating,
    visibility,
    commentAsKudos,
  ]);

  const onEscape = useCallback(() => {
    clearComment();
    setEnterCommentActive(false);
  }, [clearComment, setEnterCommentActive]);

  function onEditorStateChange(newEditorState) {
    updateEditorStatePredictTagsAndVisbility({
      editorState,
      newEditorState,
      setEditorState,
      visibilityManuallyUpdated,
      onAttributePredictionSuccess: (tags) => {
        updateTags(tags);
      },
      onVisibilityPredictionSuccess: (visibility) => {
        updateVisibility(visibility);
      },
    });
  }

  function openGifModal() {
    dispatch(openGifModalAction({ onSelect: setGif }));
  }

  return loading ? (
    <div />
  ) : (
    <Container className={className} fullWidth>
      <EnterComment
        isEnterCommentActive={isEnterCommentActive}
        setIsEnterCommentActive={setEnterCommentActive}
        placeholder={commentAsKudos ? 'Use @ to tag someone and # to include a competency' : 'Add a comment'}
        isLoggedInEmployeeComment
        styleType="chat"
        isDisabled={isDisabled}
        EditCommentComponent={
          <StyledEditCommentComponent
            placeholder={commentAsKudos ? 'Use @ to tag someone and # to include a competency' : 'Add a comment'}
            onEnter={addComment}
            // handleBlur={addComment}
            onEscape={onEscape}
            editorState={editorState}
            setEditorState={onEditorStateChange}
            loading={isCommentUpdating}
            uploadedFile={uploadedFile}
            setUploadedFile={setUploadedFile}
            MediaPreview={
              <MediaPreview
                fileUrl={fileUrl}
                deleteFile={deleteFile}
                uploading={uploading}
                uploadFile={uploadFile}
                setGif={setGif}
                gifUrl={uploadedGif ? uploadedGif.url : null}
              />
            }
            UploadTypesView={
              <UploadTypesView
                uploadFile={uploadFile}
                uploadedImage={uploadedFile}
                toggleGifModal={openGifModal}
                gifUrl={uploadedGif ? uploadedGif.url : null}
              />
            }
            VisibilityComponent={null}
            TagRecommendations={null}
            SentimentComponent={null}
            KudosComponent={null}
          />
        }
      />
    </Container>
  );
};

const Container = styled.div`
  flex: 0 0 auto;
  width: ${(props) => (props.fullWidth ? '100%' : '80%')};
  margin: 0 0rem -0.5rem 0rem;
  margin-bottom: 0;
  padding: 1rem 0rem;
  // background: ${(props) => props.theme.palette.background.lighterBlue};
`;

const StyledEditCommentComponent = styled(EditCommentComponent)`
  background: white;
`;

export default withRouter(CreateCompetencyDiscussion);
